import { useCandidateRedux } from "#candidate/redux/slice"
import {
	candidateEvaluationDelete,
	candidateEvaluationGet,
} from "#candidate/services/api"
import { useApi } from "#hooks/useApi"
import { Box, Stack } from "@mui/material"
import { useMemo, useState } from "react"

import { STATUS } from "#candidate/constants"
import ButtonIcon from "#common/buttons/ButtonIcon"
import CircularSkeleton from "#common/skeleton/CircularSkeleton"
import { PERFILES } from "#constants/Strings"
import { DialogMaster } from "#core/components/dialog/Dialog"
import ErrorResponse from "#core/pages/ErrorResponse"
import { useApiSend } from "#hooks/useApiSend"
import { UseHasPermission } from "#hooks/useHasPermission"
import DeleteIcon from "@mui/icons-material/Delete"
import Card from "@mui/material/Card"
import CardContent from "@mui/material/CardContent"
import Typography from "@mui/material/Typography"
import { useRefreshCandidate } from "#candidate/redux/actions"

export default function CandidateDetailsEvaluation({ autoFetch }) {
	const { candidateId } = useCandidateRedux()
	const params = useMemo(() => ({ id: candidateId }), [candidateId])
	const { data, loading, error } = useApi(candidateEvaluationGet, { params, autoFetch })
	const evaluation = useMemo(() => data?.data || [], [data])

	if (loading) return <CircularSkeleton />
	if (error) return <ErrorResponse api={error} />

	return (
		<Box>
			<Stack spacing={2} direction={"row"}>
				{evaluation.map((item) => (
					<MediaCard key={item.id} datum={item} />
				))}
			</Stack>
		</Box>
	)
}

function MediaCard({ datum }) {
	const [open, setOpen] = useState(false)
	const { candidate } = useCandidateRedux()
	const is_rh = UseHasPermission(PERFILES.RH)

	let showButton = false
	if (datum["evaluator_type"] === 2 && candidate.status === STATUS.BOSS_INTERVIEW) {
		showButton = true
	}
	if (
		datum["evaluator_type"] === 1 &&
		[STATUS.HR_INTERVIEW, STATUS.SCHEDULED_BY_BOSS].includes(candidate.status)
	) {
		showButton = true
	}
	showButton = showButton && is_rh

	return (
		<Card
			elevation={3}
			sx={{
				width: "100%",
				borderRadius: 2,
				transition: "transform 0.2s, box-shadow 0.2s",
			}}
		>
			<CardContent sx={{ p: 3 }}>
				{/* Header */}
				<Box
					sx={{ mb: 3, pb: 2, borderBottom: "2px solid", borderColor: "primary.main" }}
				>
					<Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
						<Typography
							variant="h6"
							component="div"
							fontWeight={600}
							color="primary.main"
							sx={{ mb: 1 }}
						>
							{datum?.evaluator_type_name}
						</Typography>
						{showButton && (
							<ButtonIcon
								icon={<DeleteIcon />}
								color="error"
								title="Eliminar entrevista"
								onClick={() => setOpen(true)}
							/>
						)}
					</Box>
					<Stack direction="row" spacing={2} alignItems="center">
						<Box>
							<Typography variant="caption" color="text.secondary" display="block">
								Calificación
							</Typography>
							<Typography variant="h5" fontWeight={700} color="success.main">
								{datum?.score}
							</Typography>
						</Box>
					</Stack>
				</Box>

				{/* Observations */}
				{datum?.observations && (
					<Box sx={{ mb: 3 }}>
						<Typography
							variant="subtitle2"
							fontWeight={600}
							color="text.primary"
							sx={{ mb: 1, textTransform: "uppercase", fontSize: "0.7rem" }}
						>
							Observaciones
						</Typography>
						<Typography
							variant="body2"
							color="text.secondary"
							sx={{
								fontSize: "0.85rem",
								lineHeight: 1.6,
								fontStyle: "italic",
							}}
						>
							{datum?.observations}
						</Typography>
					</Box>
				)}

				{/* Competencies */}
				{datum?.competencies && datum.competencies.length > 0 && (
					<Box>
						<Stack spacing={1.5}>
							{datum?.competencies?.map((comp) => (
								<Card
									key={comp.id}
									variant="outlined"
									sx={{
										p: 1.5,
										backgroundColor: "grey.50",
										borderLeft: "3px solid",
										borderLeftColor:
											comp.score >= comp.expected_score ? "success.main" : "warning.main",
									}}
								>
									<Typography
										variant="body2"
										fontWeight={600}
										color="text.primary"
										sx={{ mb: 0.5 }}
									>
										{comp?.competencia_name}
									</Typography>
									<Stack direction="row" spacing={2} sx={{ mb: 0.5 }}>
										<Box>
											<Typography variant="caption" color="text.secondary">
												Obtenida:
											</Typography>
											<Typography
												component="span"
												variant="body2"
												fontWeight={600}
												color={
													comp.score >= comp.expected_score
														? "success.main"
														: "warning.main"
												}
												sx={{ ml: 0.5 }}
											>
												{comp?.score}
											</Typography>
										</Box>
										<Box>
											<Typography variant="caption" color="text.secondary">
												Esperada:
											</Typography>
											<Typography
												component="span"
												variant="body2"
												fontWeight={600}
												sx={{ ml: 0.5 }}
											>
												{comp?.expected_score}
											</Typography>
										</Box>
									</Stack>
									{comp?.observations && (
										<Typography
											variant="caption"
											color="text.secondary"
											sx={{ display: "block", mt: 0.5, fontStyle: "italic" }}
										>
											{comp?.observations}
										</Typography>
									)}
								</Card>
							))}
						</Stack>
					</Box>
				)}
			</CardContent>
			<MediaCardDelete open={open} setOpen={setOpen} interview={datum.id} />
		</Card>
	)
}

const MediaCardDelete = ({ open, setOpen, interview }) => {
	const { candidate } = useCandidateRedux()
	const { refresh } = useRefreshCandidate()
	const [disabled, setDisabled] = useState(false)
	const success = () => {
		refresh()
	}
	const end = () => {}

	const { fetch } = useApiSend(candidateEvaluationDelete, { success, end })

	const handleSubmit = () => {
		setDisabled(true)
		fetch({ id: candidate.id, interview: interview })
	}
	return (
		<DialogMaster
			open={open}
			setOpen={setOpen}
			handleSubmit={handleSubmit}
			disabled={disabled}
			title="Eliminar entrevista"
			maxWidth="xs"
		>
			<Typography textAlign="center">
				¿Estás seguro de que deseas eliminar esta entrevista?
			</Typography>
			<Typography textAlign="center" variant="subtitle1" color="error">
				Esta acción no se puede deshacer.
			</Typography>
		</DialogMaster>
	)
}
